import React, { useState } from 'react';
import { SharedBranding } from '../../../services';
import { applyClientName } from '../branding/theme_utils';

// #region ShareBrandHeaderProps
export interface ShareBrandHeaderProps {
    branding: SharedBranding;
    clientName: string;
    // Overrides the branded headline (e.g. the locked thank-you screen).
    headlineOverride?: string;
    bodyOverride?: string;
}

// #region ShareBrandHeader
// Designer-branded page masthead: optional cover band, logo, welcome copy.
// Mobile-first — centered on small screens, left-aligned from `sm` up.
export const ShareBrandHeader: React.FunctionComponent<ShareBrandHeaderProps> = (props: ShareBrandHeaderProps): JSX.Element => {
    const { branding, clientName, headlineOverride, bodyOverride } = props;
    const [coverBroken, setCoverBroken] = useState(false);
    const [logoBroken, setLogoBroken] = useState(false);

    const headline = headlineOverride ?? applyClientName(branding.welcome_headline, clientName) ?? `Hi ${clientName}, pick your favorites`;
    const body = bodyOverride ?? applyClientName(branding.welcome_body, clientName);
    const showCover = !!branding.cover_image_url && !coverBroken;
    const showLogo = !!branding.logo_url && !logoBroken;

    return (
        <header className="mb-6 overflow-hidden rounded-3xl border border-share-border bg-share-raised shadow-sm">
            {showCover && (
                // Fixed banner ratio (not a fixed pixel height) so any uploaded
                // cover crops predictably instead of stretching the masthead.
                <div className="relative w-full overflow-hidden bg-share-border/40 aspect-[16/6] max-h-56">
                    <img
                        src={branding.cover_image_url as string}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover object-center"
                        onError={() => setCoverBroken(true)}
                    />
                </div>
            )}

            {/* The logo no longer overlaps the cover — it sits below it with a
                full block of padding, so the two images always read as separate. */}
            <div className={`px-5 pb-6 sm:px-8 ${showCover ? 'pt-6 sm:pt-7' : 'pt-7'}`}>
                <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-center sm:gap-5 sm:text-left">
                    {showLogo && (
                        // The card height is fixed but its width follows the
                        // logo's natural aspect ratio — wide wordmarks are not
                        // squeezed into a square, square marks stay square.
                        <div className="flex h-16 w-auto min-w-[4rem] max-w-[11rem] shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-share-border bg-white px-2.5 py-2 shadow-sm sm:h-20 sm:max-w-[13rem]">
                            <img
                                src={branding.logo_url as string}
                                alt={branding.contact_name || 'Designer logo'}
                                className="h-full w-auto max-w-full object-contain"
                                onError={() => setLogoBroken(true)}
                            />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        {branding.contact_name && (
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-share-primary">
                                {branding.contact_name}
                            </div>
                        )}
                        <h1 className="mt-1 text-2xl font-semibold leading-tight tracking-tight text-share-text sm:text-3xl">
                            {headline}
                        </h1>
                    </div>
                </div>

                {body && (
                    <p className="mt-4 text-center text-[15px] leading-relaxed text-share-muted sm:text-left">
                        {body}
                    </p>
                )}
            </div>
        </header>
    );
};
